import { useRef, useEffect, useState, useCallback } from "react";
import React from "react";

interface ModalProps {
  closeButtonClassName?: string;
  canClickOutsideToClose?: boolean;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
  isFullscreen?: boolean;
  roundedClass?: string;
  colorClass?: string;
  animationClass?: string;
  position?: "center" | "start" | "end";
  widthClass?: string;
  heightClass?: string;
  onOk?: () => void;
  id?: any
  ref?: React.RefObject<HTMLDivElement> | any;
}

export const NewModal: React.FC<ModalProps> = ({
  closeButtonClassName,
  canClickOutsideToClose = true,
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = false,
  isFullscreen = false,
  roundedClass = "rounded-t-3xl",
  colorClass = "bg-white/60 dark:bg-gray-900",
  animationClass = "grow-in",
  position = "end",
  widthClass = "w-full max-w-[450px]",
  heightClass = "h-auto",
  onOk = () => { },
  id,
  ref
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback(() => {
    setIsAnimating(true);
    setIsVisible(false);

    setTimeout(() => {
      onClose();
      setIsAnimating(false);
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(false);
      // Use requestAnimationFrame for proper DOM animation timing
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen && !isAnimating) return null;

  // Map position prop to Tailwind align-items classes
  const positionClass =
    position === "center"
      ? "items-center"
      : position === "start"
        ? "items-start"
        : "items-end";

  const contentClasses = isFullscreen
    ? "w-full h-full"
    : `relative ${widthClass} ${heightClass} ${roundedClass} ${colorClass}`;

  // Determine animation class based on visibility state
  const currentAnimationClass = isVisible ? animationClass : (isAnimating ? "grow-out" : "");

  return (
    <div
      ref={ref}
      id={id}
      className={`fixed inset-0 flex ${positionClass} justify-center overflow-y-auto modal z-[999]`}
      onClick={canClickOutsideToClose ? handleClose : undefined}
    >
      {!isFullscreen && (
        <div className="fixed inset-0 bg-[#171820B2]/70"></div>
      )}
      <div
        ref={modalRef}
        className={`${contentClasses} ${className} ${currentAnimationClass} mb-0`}
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            onClick={handleClose}
             className={`absolute right-4 top-3 z-999 flex h-9.5 w-9.5 items-center justify-center rounded-full sm:right-[15px] sm:top-3 sm:h-11 sm:w-11 ${closeButtonClassName}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 21.25C17.1086 21.25 21.25 17.1086 21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25Z" stroke="white" stroke-width="1.5" />
              <path d="M8.875 8.875L15.125 15.125M15.125 8.875L8.875 15.125" stroke="white" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </button>
        )}
        <div>{children}</div>
      </div>
    </div>
  );
};

